import type { AdCampaignDraft } from '@/redux/slices/adManager/adManager';
import type { LomavisCampaignReviewRow1 } from '@/lomavisSharedUi/templates/sections/LomavisCampaignBuilder1/campaignReviewTable';
import { formatCurrencyAmountLabel } from '@/lomavisSharedUi/molecules/inputs/LomavisBudgetControls1';

import {
  draftStatusKeyByValue,
  EMPTY_CAMPAIGN_THUMBNAIL
} from './constants';
import type { TranslateFn } from './types';

export const getAdCampaignDraftStatusKey = (
  draft?: Pick<
    AdCampaignDraft,
    'upload_to_platform_status' | 'upload_to_platform_status_key'
  > | null
) => {
  if (
    draft?.upload_to_platform_status_key &&
    ['draft', 'publishing', 'published', 'failed'].includes(
      draft.upload_to_platform_status_key
    )
  ) {
    return draft.upload_to_platform_status_key;
  }

  if (typeof draft?.upload_to_platform_status === 'number') {
    return draftStatusKeyByValue[draft.upload_to_platform_status] || 'draft';
  }

  return 'draft';
};

export const getAdCampaignDraftStatusLabel = (
  draft?: Pick<
    AdCampaignDraft,
    | 'upload_to_platform_status'
    | 'upload_to_platform_status_key'
    | 'upload_to_platform_status_label'
  > | null,
  isPublishing = false,
  t: TranslateFn = (_key, fallback) => fallback
) => {
  const statusLabels = {
    draft: t('adManager.status.draft', 'Draft'),
    publishing: t('adManager.status.publishing', 'Publishing'),
    published: t('adManager.status.published', 'Published'),
    failed: t('adManager.status.failed', 'Failed')
  };

  if (isPublishing) return statusLabels.publishing;

  return statusLabels[getAdCampaignDraftStatusKey(draft)];
};

export const isAdCampaignDraftMutable = (
  draft?: Pick<
    AdCampaignDraft,
    'upload_to_platform_status' | 'upload_to_platform_status_key'
  > | null
) => ['draft', 'failed'].includes(getAdCampaignDraftStatusKey(draft));

export const hasAdCampaignDraftMetaObjects = (
  draft?: Pick<
    AdCampaignDraft,
    | 'meta_campaign_id'
    | 'meta_adset_id'
    | 'meta_ad_id'
    | 'meta_creative_id'
  > | null
) =>
  Boolean(
    draft &&
      [
        draft.meta_campaign_id,
        draft.meta_adset_id,
        draft.meta_ad_id,
        draft.meta_creative_id
      ].some((metaId) => String(metaId || '').trim())
  );

export const buildReviewRows = (
  drafts: AdCampaignDraft[],
  currency: string,
  publishingDraftUuid: string | null = null,
  t: TranslateFn = (_key, fallback) => fallback
): LomavisCampaignReviewRow1[] =>
  drafts.map((draft) => {
    const isPublishing = Boolean(
      publishingDraftUuid && draft.uuid === publishingDraftUuid
    );
    const isMutable = isPublishing ? false : isAdCampaignDraftMutable(draft);
    const thumbnailUrl =
      draft.thumbnail_url || draft.media_files?.[0]?.preview_url || '';
    const thumbnail = thumbnailUrl
      ? `url("${thumbnailUrl}") center / cover`
      : EMPTY_CAMPAIGN_THUMBNAIL;
    const amount =
      draft.budget_amount_cents === null ||
      draft.budget_amount_cents === undefined
        ? '-'
        : formatCurrencyAmountLabel(
            draft.budget_amount_cents / 100,
            currency || draft.currency || ''
          );

    return {
      id: draft.uuid,
      canDelete: isMutable,
      canEdit: isMutable,
      isDisabled: draft.is_disabled === true,
      campaign: draft.name,
      end: draft.end_date || '-',
      objective: draft.objective
        ? t(`adManager.objectives.${draft.objective}`, draft.objective)
        : '-',
      platforms: (draft.placements || [])
        .map((placement) =>
          placement.startsWith('instagram') ? 'instagram' : 'facebook'
        )
        .filter(
          (platform, index, platforms) => platforms.indexOf(platform) === index
        ) as LomavisCampaignReviewRow1['platforms'],
      spend: amount,
      start: draft.start_date || '-',
      status: getAdCampaignDraftStatusLabel(draft, isPublishing, t),
      statusKey: isPublishing ? 'publishing' : getAdCampaignDraftStatusKey(draft),
      thumbnail
    };
});
